import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from 'authConfig.js';
import { useHistory } from 'react-router-dom';
import LogoImage from "assets/css/loginpassLogo.svg";
import axios from 'axios';
import { apiPath } from "apiurl.js";
import packageJson from '../../../package.json';
const { version } = packageJson;
import { 
  Button, Modal, Form, Alert, Spinner
} from "react-bootstrap";
import "./LoginPage.css"

function Login() {
  const { instance } = useMsal();
  const [userInfo, setUserInfo] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useHistory();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    workEmail: '',
    title: '',
    organizationName: '',
    reasonForAccess: '',
  });
  const [submissionStatus, setSubmissionStatus] = useState({ 
    success: false, 
    message: '' 
  });
  const [activeTab, setActiveTab] = useState('signin');
  const isDevBypassEnabled = process.env.REACT_APP_SKIP_AZURE_AD === 'true';

  const isUserCancelledAuth = (authError) => {
    const errorCode = authError?.errorCode || authError?.code || '';
    const message = authError?.message || '';

    return (
      errorCode === 'user_cancelled' ||
      errorCode === 'user_cancelled_error' ||
      message.toLowerCase().includes('user_cancelled')
    );
  };

  const handleDevBypassLogin = () => {
    const bypassUserInfo = {
      email: process.env.REACT_APP_DEV_USER_EMAIL || 'dev.user@cdc.gov',
      fullName: process.env.REACT_APP_DEV_USER_NAME || 'PASS Dev User',
      jobTitle: process.env.REACT_APP_DEV_USER_JOB_TITLE || 'Developer',
      department: process.env.REACT_APP_DEV_USER_DEPARTMENT || 'PASS Engineering',
      phone: process.env.REACT_APP_DEV_USER_PHONE || 'Not specified',
      tokenId: 'development-auth-bypass'
    };

    setUserInfo(bypassUserInfo);
    sessionStorage.setItem('userInfo', JSON.stringify(bypassUserInfo));
    navigate.push('/pass/dashboard');
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      // loginRedirect navigates the page away — AuthProvider handles the
      // redirect response when the page loads back and navigates to dashboard.
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      if (isUserCancelledAuth(error)) {
        setError('Sign-in was cancelled. Please try again when you are ready.');
        return;
      }
      console.error('Authentication error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequestForm = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${apiPath}/api/submit-form`, formData);
      setSubmissionStatus({ success: true, message: 'Your request has been successfully submitted!' });
      setFormData({ fullName: '', workEmail: '', title: '', organizationName: '', reasonForAccess: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionStatus({ 
        success: false, 
        message: 'Error submitting the request. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const denied = sessionStorage.getItem('authDeniedMessage');
    if (denied === 'access_denied') {
      sessionStorage.removeItem('authDeniedMessage');
      setError(
        'You do not have access to PASS. If you believe this is an error, please contact IT Support.'
      );
    }
  }, []);

  useEffect(() => {
    let timeoutId;
    if (submissionStatus.success) {
      timeoutId = setTimeout(() => {
        setShowRequestForm(false);
        window.location.reload();
      }, 2000);
    }
    return () => clearTimeout(timeoutId);
  }, [submissionStatus.success]);

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-left">
          <img src={LogoImage} alt="PASS Logo" className="logo-image logo-large" />
          <div className="login-features">
            <h2>Secure Access Portal</h2>
            <ul>
              <li>
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>Single Sign-On Authentication</span>
              </li>
              <li>
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>Enterprise-Grade Security</span>
              </li>
              <li>
                <div className="feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>CDC Integrated Access System</span>
              </li>
              <div className="login-version-tag">v{version}</div>
            </ul>
          </div>
          <div className="wave-animation">
            <div className="wave wave1"></div>
            <div className="wave wave2"></div>
            <div className="wave wave3"></div>
          </div>
        </div>

        <div className="login-right">
          <div className="tabs-container">
            <div className="tabs-header">
              <button 
                className={`tab-button ${activeTab === 'signin' ? 'active' : ''}`}
                onClick={() => setActiveTab('signin')}
              >
                Sign In
              </button>
              <button 
                className={`tab-button ${activeTab === 'request' ? 'active' : ''}`}
                onClick={() => setActiveTab('request')}
              >
                Request Access
              </button>
            </div>

            <div className="tabs-content">
              {activeTab === 'signin' ? (
                <div className="signin-tab">
                  <h1>Welcome Back</h1>
                  <p className="subtitle">Access PASS with your CDC Microsoft authentication</p>
                  
                  {error && <Alert variant="danger">{error}</Alert>}
                  
                  <div className="signin-options">
                    <Button
                      className="signin-button microsoft-button"
                      onClick={handleLogin}
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <>
                          <div className="button-icon">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.5 3v8.5H3V3h8.5zm9.5 0v8.5h-8.5V3H21zm-9.5 9.5V21H3v-8.5h8.5zm9.5 0V21h-8.5v-8.5H21z" fill="currentColor" />
                            </svg>
                          </div>
                          <span>Sign in with Microsoft</span>
                        </>
                      )}
                    </Button>

                    {isDevBypassEnabled && (
                      <Button
                        className="signin-button bypass-button"
                        onClick={handleDevBypassLogin}
                        disabled={loading}
                      >
                        Skip Azure AD (Dev Bypass)
                      </Button>
                    )}
                  </div>
                  
                  <div className="additional-options">
                    <p>
                      Don't have access? 
                      <button 
                        className="text-button"
                        onClick={() => setActiveTab('request')}
                      >
                        Request now
                      </button>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="request-tab">
                  <h1>Request Access</h1>
                  <p className="subtitle">Complete the form to request system access</p>
                  
                  {submissionStatus.message && (
                    <Alert variant={submissionStatus.success ? "success" : "danger"}>
                      {submissionStatus.message}
                    </Alert>
                  )}
                  
                  <Form onSubmit={handleSubmitRequestForm}>
                    <div className="form-floating mb-3">
                      <Form.Control
                        id="fullName"
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={updateFormData}
                        required
                        placeholder="John Doe"
                        className="custom-input"
                      />
                      <label htmlFor="fullName">Full Name</label>
                    </div>

                    <div className="form-floating mb-3">
                      <Form.Control
                        id="workEmail"
                        type="email"
                        name="workEmail"
                        value={formData.workEmail}
                        onChange={updateFormData}
                        required
                        placeholder="name@example.com"
                        className="custom-input"
                      />
                      <label htmlFor="workEmail">Work Email</label>
                    </div>

                    <div className="form-floating mb-3">
                      <Form.Control
                        id="title"
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={updateFormData}
                        required
                        placeholder="e.g. Epidemiologist"
                        className="custom-input"
                      />
                      <label htmlFor="title">Title</label>
                    </div>

                    <div className="form-floating mb-3">
                      <Form.Control
                        id="organizationName"
                        type="text"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={updateFormData}
                        required
                        placeholder="e.g. CDC / State Health Department"
                        className="custom-input"
                      />
                      <label htmlFor="organizationName">Organization Name</label>
                    </div>

                    <div className="form-floating mb-4">
                      <Form.Control
                        id="reasonForAccess"
                        as="textarea"
                        name="reasonForAccess"
                        value={formData.reasonForAccess}
                        onChange={updateFormData}
                        required
                        placeholder="Please describe why you need access"
                        className="custom-input custom-textarea"
                        style={{ height: "100px" }}
                      />
                      <label htmlFor="reasonForAccess">Reason for Access</label>
                    </div>
                    
                    <Button 
                      className="submit-request-button" 
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Submit Request'
                      )}
                    </Button>
                  </Form>
                  
                  <div className="additional-options">
                    <p>
                      Already have access? 
                      <button 
                        className="text-button"
                        onClick={() => setActiveTab('signin')}
                      >
                        Sign in
                      </button>
                    </p>
                  </div>
                </div>
              )}
              
              <div className="login-footer">
                <p>Need help? Contact <a href="#">IT Support</a></p>
                <p className="copyright">© 2025 CDC - All Rights Reserved</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
